import { Languages, type LanguageT } from '~/localization/i18n';

/**
 * Client-safe helpers for the multi-state site: URL shapes, status values, and
 * the wording that names the states we serve. Kept out of `content.server` so
 * components and meta functions can import them.
 */

export const STATE_STATUS = { LIVE: 'live', COMING_SOON: 'coming_soon' } as const;
export type StateStatus = (typeof STATE_STATUS)[keyof typeof STATE_STATUS];

/** Every URL that existed before the expansion named a Georgia county. */
export const LEGACY_AREA_STATE = 'GA';

export const LOCATIONS_PATH = '/locations';

export function statePath(stateSlug: string): string {
  return `${LOCATIONS_PATH}/${stateSlug}`;
}

export function areaPath(stateSlug: string, areaSlug: string): string {
  return `${LOCATIONS_PATH}/${stateSlug}/${areaSlug}`;
}

/** The fields a state needs to be named and linked; satisfied by a `states` row. */
export type StateLike = {
  code: string;
  slug: string;
  name_en: string;
  name_es: string;
  status: string;
};

export function isLive(state: Pick<StateLike, 'status'>): boolean {
  return state.status === STATE_STATUS.LIVE;
}

export function stateName(state: Pick<StateLike, 'name_en' | 'name_es'>, locale: LanguageT | string): string {
  return locale === Languages.ES ? state.name_es : state.name_en;
}

/**
 * "Georgia", "Georgia and Virginia", "Georgia, Virginia and Texas" — and the
 * Spanish equivalents. No serial comma, matching the site's existing copy.
 */
export function joinNames(names: string[], locale: LanguageT | string): string {
  const and = locale === Languages.ES ? 'y' : 'and';
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
}

/**
 * Titles that name every live state stay readable only while there are few of
 * them; past this many, a general page says "across the states we serve" and
 * leaves the state names to the state pages.
 */
export const MAX_STATES_IN_TITLE = 3;

export function liveStateNames(states: StateLike[], locale: LanguageT | string): string[] {
  return states.filter(isLive).map((state) => stateName(state, locale));
}

/** The live states as a phrase for a title, or null when there are too many to list. */
export function liveStatesPhrase(states: StateLike[], locale: LanguageT | string): string | null {
  const names = liveStateNames(states, locale);
  if (names.length === 0 || names.length > MAX_STATES_IN_TITLE) return null;
  return joinNames(names, locale);
}

/** The value a form posts when the family's state is not one we list. */
export const OTHER_STATE = 'other';

/** Every visible state, then "Other", for a form's optional State select. */
export function stateSelectOptions(
  states: StateLike[],
  locale: LanguageT | string,
  otherLabel: string
): { value: string; label: string }[] {
  return [
    ...states.map((state) => ({ value: state.code, label: stateName(state, locale) })),
    { value: OTHER_STATE, label: otherLabel },
  ];
}

/** How a county-directory entry is named: "Harris County", "City of Richmond", "Condado de Harris". */
export function countyLabel(name: string, kind: 'county' | 'city', locale: LanguageT | string): string {
  const isEs = locale === Languages.ES;
  if (kind === 'city') return isEs ? `Ciudad de ${name}` : `City of ${name}`;
  return isEs ? `Condado de ${name}` : `${name} County`;
}

/** Case- and accent-insensitive form of a name or query, so "dona ana" finds "Doña Ana". */
export function foldName(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

/** The anchor of a state page's full county list. */
export const COUNTIES_ANCHOR = 'counties';

/** The shortest place search that returns results; shared by the server search and the form. */
export const MIN_QUERY_LENGTH = 2;
